/**
 * From/To bounds of the "… within range" rules (tpls 67/68/69) and their `tz` arg.
 *
 * `tz` says which clock the bounds are written in (backend demo/lib/events/dt_match.py):
 * - 'utc'   — datetime bounds are ISO instants (`…Z`); values are compared in UTC.
 * - 'local' — datetime bounds carry no offset (`YYYY-MM-DDTHH:mm:ss`); values are
 *             compared on their own wall clock (the tour's zone for localized fields).
 * A rule without `tz` keeps its template's historical mode: UTC for datetime,
 * local for date and time.
 *
 * The datetime picker always runs with timezone="UTC", so it shows the stored
 * wall clock as is, whatever the browser's zone: in 'utc' mode that is the UTC
 * time, in 'local' mode the wall clock as written. Date and time bounds are
 * plain strings with no clock to convert.
 */
import moment from 'moment';

import { DATETIME_WITHIN_RANGE, TIME_WITHIN_RANGE } from './utils';

export const TZ_UTC = 'utc';
export const TZ_LOCAL = 'local';

export function effectiveTz(tpl, tz) {
    if (tz === TZ_UTC || tz === TZ_LOCAL) return tz;
    return tpl === DATETIME_WITHIN_RANGE ? TZ_UTC : TZ_LOCAL;
}

/** A stored bound → a moment for the picker (null when absent). */
export function parseDtBound(tpl, tz, s) {
    if (!s) return null;
    if (tpl === TIME_WITHIN_RANGE) return moment(s, 'HH:mm:ss');
    if (tpl !== DATETIME_WITHIN_RANGE) return moment(s);
    if (effectiveTz(tpl, tz) === TZ_LOCAL) {
        // the wall clock as written; an offset (never written in this mode) is ignored,
        // as the matcher does
        return moment.parseZone(s).utc(true);
    }
    return moment.utc(s); // no offset = UTC, as the matcher reads it
}

/** A picker value → the stored bound (null when cleared or invalid). */
export function serializeDtBound(tpl, tz, v) {
    if (!v || !v.isValid()) return null;
    if (tpl === TIME_WITHIN_RANGE) return v.format('HH:mm:ss');
    if (tpl !== DATETIME_WITHIN_RANGE) return v.format('YYYY-MM-DD');
    if (effectiveTz(tpl, tz) === TZ_LOCAL) return v.clone().utc().format('YYYY-MM-DDTHH:mm:ss');
    return v.toISOString();
}

/** Rewrite a stored bound for another mode, keeping the wall clock the editor shows. */
export function retzDtBound(tpl, fromTz, toTz, s) {
    if (!s || tpl !== DATETIME_WITHIN_RANGE) return s;
    return serializeDtBound(tpl, toTz, parseDtBound(tpl, fromTz, s));
}
